import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AgGridTable, Pagination, DateRangePicker } from 'components/common';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MdGridOn } from 'react-icons/md';
import { FaSearch, FaTimes, FaSyncAlt } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { getPriceMatrices, exportPriceMatrix } from 'redux/crm/project/priceMatrixSlice';
import { debounce } from 'helpers/debounce';

const SEARCH_FIELD = 'project_name_or_project_q_number_or_project_j_number_cont';

const EMPTY_DATES = { contract_from: '', contract_to: '', created_from: '', created_to: '' };

// Map the inline date filters to ransack params.
const dateFilterParams = (d) => ({
  ...(d.contract_from ? { project_contract_date_gteq: d.contract_from } : {}),
  ...(d.contract_to   ? { project_contract_date_lteq: d.contract_to }   : {}),
  ...(d.created_from  ? { created_at_gteq: d.created_from }             : {}),
  // created_at is a datetime — include the whole end day.
  ...(d.created_to    ? { created_at_lteq: `${d.created_to} 23:59:59` } : {}),
});

// Price matrices are grouped by their parent project. A project (deal_id) can
// have several price matrices; the backend returns them contiguously so we can
// collapse the repeated project columns into a single visual block per project.
const GROUP_KEY = 'deal_id';

// True when this row is the first of a new project group (deal_id differs from
// the row above). Used to blank repeated project cells and draw group dividers.
const isGroupStart = (params) => {
  const idx = params.node?.rowIndex;
  if (idx == null || idx === 0) return true;
  const prev = params.api.getDisplayedRowAtIndex(idx - 1)?.data;
  return !prev || prev[GROUP_KEY] !== params.data?.[GROUP_KEY];
};

// Number of price matrices belonging to the same project as this row.
const groupSize = (params) => {
  const dealId = params.data?.[GROUP_KEY];
  let count = 0;
  params.api.forEachNode((node) => {
    if (node.data?.[GROUP_KEY] === dealId) count += 1;
  });
  return count;
};

const getNested = (data, path) =>
  path.split('.').reduce((obj, key) => (obj == null ? obj : obj[key]), data);

// Renders a project-level value only on the first row of each group; repeated
// rows stay blank so the project reads as one block.
const groupedValueGetter = (path) => (params) =>
  isGroupStart(params) ? getNested(params.data, path) ?? '' : '';

const formatNumber = (v) =>
  v == null || v === '' ? '' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatMoney = (v) => (v == null || v === '' ? '' : `$${formatNumber(v)}`);

const moneyCellStyle = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

// J number on the group's first row, linked to the project.
const JNumberCell = (params) => {
  if (!isGroupStart(params)) return '';
  const project = params.data?.project;
  const jNumber = project?.job_number;
  if (!jNumber) return '';
  return project.id ? (
    <Link to={`/crm/projects/${project.id}`} className="tw-font-medium tw-text-primary-600 hover:tw-underline">
      {jNumber}
    </Link>
  ) : (
    jNumber
  );
};

// Project name on the group's first row (plain text) with a "× N" badge when
// the project has more than one price matrix.
const ProjectNameCell = (params) => {
  if (params.node.rowPinned) return <span className="tw-font-black tw-text-primary-800 tw-uppercase tw-tracking-wide">Total</span>;
  if (!isGroupStart(params)) return '';
  const project = params.data?.project;
  if (!project?.name) return '';
  const count = groupSize(params);
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-font-medium tw-text-slate-700">{project.name}</span>
      {count > 1 && (
        <span className="tw-inline-flex tw-items-center tw-justify-center tw-px-1.5 tw-h-[18px] tw-rounded-xl tw-bg-primary-100 tw-text-primary-600 tw-text-[11px] tw-font-bold tw-leading-none">
          ×{count}
        </span>
      )}
    </span>
  );
};

// Customer name on the group's first row, linked to the customer when present.
const CustomerCell = (params) => {
  if (!isGroupStart(params)) return '';
  const project = params.data?.project;
  if (!project?.customer_name) return '';
  return project.customer_id ? (
    <Link to={`/crm/customers/${project.customer_id}`} className="tw-text-primary-600 hover:tw-underline">
      {project.customer_name}
    </Link>
  ) : (
    project.customer_name
  );
};

// Contract / addendum the price matrix belongs to (per row, not grouped).
const ContractCell = (params) => {
  if (params.node.rowPinned) return '';
  const contract = params.data?.contract;
  if (!contract) return <span className="tw-text-slate-300">—</span>;
  const isAddendum = contract.contract_type === 'addendum';
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-text-slate-700">{contract.contract_number}</span>
      <span
        className={`tw-inline-flex tw-items-center tw-px-1.5 tw-h-[18px] tw-rounded-sm tw-text-[10px] tw-font-semibold tw-leading-none tw-uppercase ${
          isAddendum ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-emerald-100 tw-text-emerald-700'
        }`}
      >
        {isAddendum ? 'Addendum' : 'Contract'}
      </span>
    </span>
  );
};

// Row number, pinned with Q/J so the project identity stays visible on scroll.
const NO_COL = {
  headerName: 'No.',
  valueGetter: (params) => (params.node.rowPinned ? '' : (params.node.rowIndex ?? 0) + 1),
  width: 56, minWidth: 56, maxWidth: 64, flex: 0,
  pinned: 'left', lockPinned: true,
  sortable: false, filter: false, resizable: false,
  cellStyle: { color: '#94a3b8', textAlign: 'center' },
};

const COLUMN_DEFS = [
  NO_COL,
  { field: 'project.job_number', headerName: 'J Number', minWidth: 90, maxWidth: 120, flex: 0, pinned: 'left', cellRenderer: JNumberCell },
  { field: 'project.name', headerName: 'Project Name', width: 240, cellRenderer: ProjectNameCell },
  { field: 'project.customer_name', headerName: 'Customer', width: 240, cellRenderer: CustomerCell },
  { field: 'factory_visit_fee', headerName: 'Factory Visit', width: 140, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'add_marketing_fee', headerName: 'Marketing Fee', width: 140, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'erection_charge', headerName: 'Erection', width: 140, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'pe_fee', headerName: 'PE Fee', width: 140, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'container_number', headerName: 'Containers', width: 120, cellStyle: { textAlign: 'right' }, valueFormatter: (p) => formatNumber(p.value) },
  { field: 'unit_cost', headerName: 'Unit Cost', width: 130, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'transportation_charge', headerName: 'Transportation', width: 140, cellStyle: moneyCellStyle, valueFormatter: (p) => formatMoney(p.value) },
  { field: 'contract', headerName: 'Contract / Addendum', width: 200, cellRenderer: ContractCell },
  { field: 'project.contract_date', headerName: 'Contract Date', width: 160, valueGetter: groupedValueGetter('project.contract_date') },
  { field: 'created_by', headerName: 'Created By', width: 120 },
  { field: 'created_date', headerName: 'Created At', width: 170 },
];

// Highlight the pinned summary (TOTAL) row so it stands out.
const getRowStyle = (params) =>
  params.node.rowPinned
    ? { fontWeight: 700, background: '#e0e7ff', color: '#3730a3', borderTop: '2px solid #6366f1' }
    : undefined;

const toQS = (params) =>
  new URLSearchParams(
    Object.entries(params).reduce((acc, [k, v]) => {
      if (v !== null && v !== undefined && v !== '') acc[k] = v;
      return acc;
    }, {})
  ).toString();

const PriceMatrixPage = () => {
  const dispatch = useDispatch();

  const rowData   = useSelector((s) => s.crm_project_price_matrix.data);
  const paginate  = useSelector((s) => s.crm_project_price_matrix.paginate);
  const loading   = useSelector((s) => s.crm_project_price_matrix.loading);
  const exporting = useSelector((s) => s.crm_project_price_matrix.exporting);

  const totalRows  = paginate?.total_rows || 0;
  const totalPages = paginate?.total_pages || Math.ceil(totalRows / 50) || 1;

  // Summary footer — totals across the visible rows (Unit Cost is per-container,
  // so it is intentionally left blank).
  const summaryRow = useMemo(() => {
    if (!rowData?.length) return null;
    const t = rowData.reduce((a, r) => ({
      factory_visit_fee:     a.factory_visit_fee     + (Number(r.factory_visit_fee) || 0),
      add_marketing_fee:     a.add_marketing_fee     + (Number(r.add_marketing_fee) || 0),
      erection_charge:       a.erection_charge       + (Number(r.erection_charge) || 0),
      pe_fee:                a.pe_fee                + (Number(r.pe_fee) || 0),
      container_number:      a.container_number      + (Number(r.container_number) || 0),
      transportation_charge: a.transportation_charge + (Number(r.transportation_charge) || 0),
    }), { factory_visit_fee: 0, add_marketing_fee: 0, erection_charge: 0, pe_fee: 0, container_number: 0, transportation_charge: 0 });
    return [{ ...t, unit_cost: null, _total: true }];
  }, [rowData]);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort]                = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]   = useState('');
  const [dateFilters, setDateFilters] = useState(EMPTY_DATES);

  const activeFilterParamsRef = useRef({});
  const searchRef = useRef('');

  const buildParamsObj = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage,
      'q[s]': `${sort.attribute} ${sort.direction}`,
      ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort]
  );

  const fetchData = useCallback((params) => {
    dispatch(getPriceMatrices({ params: toQS(params) }));
  }, [dispatch]);

  useEffect(() => { fetchData(buildParamsObj()); }, [fetchData, buildParamsObj]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({ page: 1, per_page: perPage, 'q[s]': `${sort.attribute} ${sort.direction}`, ...(val ? { [SEARCH_FIELD]: val } : {}), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({ page: 1, per_page: perPage, 'q[s]': `${sort.attribute} ${sort.direction}`, ...activeFilterParamsRef.current });
  };

  const handleRangeChange = (fromKey, toKey, { startDate, endDate }) => {
    const next = { ...dateFilters, [fromKey]: startDate || '', [toKey]: endDate || '' };
    setDateFilters(next);
    const params = dateFilterParams(next);
    activeFilterParamsRef.current = params;
    setPage(1);
    fetchData({ page: 1, per_page: perPage, 'q[s]': `${sort.attribute} ${sort.direction}`, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...params });
  };

  const handleResetFilter = () => {
    setDateFilters(EMPTY_DATES);
    activeFilterParamsRef.current = {};
    setPage(1);
    fetchData({ page: 1, per_page: perPage, 'q[s]': `${sort.attribute} ${sort.direction}`, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}) });
  };

  const hasDateFilter = Object.values(dateFilters).some(Boolean);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdGridOn className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Price Matrix</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">View and manage price matrix records for all projects.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button type="button" onClick={() => fetchData(buildParamsObj())} disabled={loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} />
              Reload
            </button>
            <button type="button" onClick={() => dispatch(exportPriceMatrix(toQS(buildParamsObj())))} disabled={exporting || loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-48 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Project name, Q/J number..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Inline date-range filters (taiwincss DateRangePicker) */}
          <DateRangePicker
            label="Contract date"
            startDate={dateFilters.contract_from}
            endDate={dateFilters.contract_to}
            onChange={(r) => handleRangeChange('contract_from', 'contract_to', r)}
            className="tw-flex-shrink-0"
          />
          <DateRangePicker
            label="Created date"
            startDate={dateFilters.created_from}
            endDate={dateFilters.created_to}
            onChange={(r) => handleRangeChange('created_from', 'created_to', r)}
            className="tw-flex-shrink-0"
          />

          {hasDateFilter && (
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1.5 tw-rounded tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium tw-flex-shrink-0">
              <FaTimes size={8} /> Clear
            </button>
          )}

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={rowData || []}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          sort={false}
          height="max(320px, calc(100dvh - 290px))"
          getRowStyle={getRowStyle}
          colDefOverride={{ flex: 0 }}
          pinnedBottomRowData={summaryRow}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />
    </div>
  );
};

export default PriceMatrixPage;
